import type { AccelSample } from '../sensors';
import { detectRuntime, isIos } from './runtime';
import { JoltAccel } from './native-accel';
import type { PluginListenerHandle } from '@capacitor/core';

/**
 * Stream accelerometer samples (gravity-included, m/s²) to a callback.
 *
 * Two implementations:
 *   - BrowserAccelSource: window 'devicemotion' listener. Used in PWA
 *     (any browser) and on iOS Safari. iOS Safari requires
 *     DeviceMotionEvent.requestPermission() from a user gesture.
 *   - NativeAccelSource: routes through the JoltAccel Capacitor plugin
 *     (Android SensorManager TYPE_ACCELEROMETER at SENSOR_DELAY_GAME).
 *     Used inside the wrapped APK because Chromium WebView throttles
 *     DeviceMotionEvent to <10 Hz unevenly (see joltstream#2).
 *
 * Returns a stop function. Subscriber survives until stop() is called.
 */
export type AccelSink = (s: AccelSample) => void;

export interface AccelSource {
  start: (sink: AccelSink) => Promise<void>;
  stop: () => Promise<void>;
  /** Rate the platform actually delivers, in Hz. -1 if unknown. */
  readonly nominalRateHz: number;
}

export class PermissionDeniedError extends Error {
  constructor() {
    super('Motion permission denied');
  }
}

class BrowserAccelSource implements AccelSource {
  readonly nominalRateHz: number;
  private handler: ((e: DeviceMotionEvent) => void) | null = null;

  constructor() {
    // Most browsers deliver DeviceMotionEvent at ~60 Hz; iOS at ~50 Hz.
    // We don't know exactly, so we declare a conservative 50.
    this.nominalRateHz = 50;
  }

  async start(sink: AccelSink): Promise<void> {
    if (typeof DeviceMotionEvent === 'undefined') {
      throw new Error('DeviceMotionEvent not available in this browser');
    }
    // iOS Safari permission gate (added in iOS 13)
    const dme = DeviceMotionEvent as unknown as {
      requestPermission?: () => Promise<'granted' | 'denied'>;
    };
    if (typeof dme.requestPermission === 'function') {
      const status = await dme.requestPermission();
      if (status !== 'granted') throw new PermissionDeniedError();
    }

    this.handler = (e: DeviceMotionEvent) => {
      const a = e.accelerationIncludingGravity;
      if (!a || a.x === null || a.y === null || a.z === null) return;
      sink({
        ax: a.x,
        ay: a.y,
        az: a.z,
        ts: e.timeStamp ? Date.now() - (performance.now() - e.timeStamp) : Date.now(),
      });
    };
    window.addEventListener('devicemotion', this.handler);
  }

  async stop(): Promise<void> {
    if (this.handler) {
      window.removeEventListener('devicemotion', this.handler);
      this.handler = null;
    }
  }
}

/**
 * Capacitor-Android path. Bypasses the WebView's throttled
 * `devicemotion` event by going through our `JoltAccel` plugin
 * (Kotlin SensorManager listener). See web/android-overlay/.
 */
class NativeAccelSource implements AccelSource {
  /** SENSOR_DELAY_GAME ≈ 50 Hz on most Android devices. */
  readonly nominalRateHz = 50;
  private handle: PluginListenerHandle | null = null;

  async start(sink: AccelSink): Promise<void> {
    this.handle = await JoltAccel.addListener('accel', (e) => {
      sink({ ax: e.x, ay: e.y, az: e.z, ts: e.ts });
    });
    await JoltAccel.start();
  }

  async stop(): Promise<void> {
    try {
      await JoltAccel.stop();
    } catch {
      // best-effort
    }
    if (this.handle) {
      await this.handle.remove();
      this.handle = null;
    }
  }
}

export function createAccelSource(): AccelSource {
  if (detectRuntime() === 'capacitor') {
    return new NativeAccelSource();
  }
  return new BrowserAccelSource();
}

/** Expose for callers that want to surface iOS-specific UX hints. */
export const accelNeedsExplicitPermission = (): boolean => {
  const dme = (typeof DeviceMotionEvent !== 'undefined' ? DeviceMotionEvent : null) as unknown as {
    requestPermission?: () => Promise<'granted' | 'denied'>;
  } | null;
  return Boolean(dme?.requestPermission) || isIos();
};
